import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Coins, User, ChevronDown, ChevronUp } from 'lucide-react';
import { Session } from '@supabase/supabase-js';

interface FloatingMenuProps {
    session: Session | null;
}

/**
 * Menú flotante inferior para desktop - Mejorado con UX/UI premium
 * Solo muestra el botón activo grande, los demás más pequeños
 * Incluye opción para minimizar/maximizar
 */
export function FloatingMenu({ session }: FloatingMenuProps) {
    const location = useLocation();
    const [isMinimized, setIsMinimized] = useState(false);

    if (!session) return null;

    const isActive = (path: string) => {
        if (path === '/dashboard' && location.pathname === '/') return true;
        return location.pathname === path;
    };

    const menuItems = [
        {
            path: '/dashboard',
            label: 'App',
            icon: LayoutDashboard,
            color: 'purple',
            gradient: 'from-purple-600 to-purple-800',
            glow: 'shadow-purple-500/50'
        },
        {
            path: '/my-cvs',
            label: 'Mis CVs',
            icon: FileText,
            color: 'emerald',
            gradient: 'from-emerald-600 to-emerald-800',
            glow: 'shadow-emerald-500/50'
        },
        {
            path: '/coins',
            label: 'Monedas',
            icon: Coins,
            color: 'yellow',
            gradient: 'from-yellow-600 to-amber-800',
            glow: 'shadow-yellow-500/50'
        },
        {
            path: '/account',
            label: 'Mi Cuenta',
            icon: User,
            color: 'indigo',
            gradient: 'from-indigo-600 to-indigo-800',
            glow: 'shadow-indigo-500/50'
        },
    ];

    return (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 hidden md:block">
            <div className={`
                relative bg-gradient-to-br from-gray-900/98 to-gray-950/98 backdrop-blur-2xl 
                border border-white/10 rounded-full shadow-2xl transition-all duration-500 ease-out
                flex items-center
                ${isMinimized ? 'p-1.5' : 'px-3 py-2'}
            `}>
                {/* Resplandor de fondo más sutil */}
                <div className={`absolute -inset-1 bg-gradient-to-r from-purple-600/10 via-blue-600/10 to-indigo-600/10 rounded-full blur-lg -z-10 transition-opacity duration-500 ${isMinimized ? 'opacity-0' : 'opacity-100'}`}></div>

                {/* Borde superior brillante */}
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent rounded-full"></div>

                {/* Items del menú - Ocultos al minimizar */}
                <div className={`flex items-center gap-2 overflow-hidden transition-all duration-500 ease-in-out ${isMinimized ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                    <div className="flex items-center gap-2">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const active = isActive(item.path);

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`group relative flex items-center gap-2 rounded-full transition-all duration-500 ease-out flex-shrink-0 ${active
                                        ? `bg-gradient-to-br ${item.gradient} px-5 py-3 shadow-lg ${item.glow} scale-105`
                                        : 'p-3 hover:bg-white/5 hover:scale-105'
                                        }`}
                                >
                                    <Icon
                                        className={`transition-all duration-300 ${active
                                            ? 'w-5 h-5 text-white'
                                            : 'w-4 h-4 text-gray-400 group-hover:text-white group-hover:scale-110'
                                            }`}
                                    />
                                    <span
                                        className={`font-medium transition-all duration-500 overflow-hidden whitespace-nowrap ${active
                                            ? 'text-white text-sm max-w-[100px] opacity-100 ml-2'
                                            : 'max-w-0 opacity-0 ml-0'
                                            }`}
                                    >
                                        {item.label}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Separador vertical sutil - Ajustado altura */}
                    <div className="w-[1px] h-6 bg-gradient-to-b from-transparent via-white/10 to-transparent mx-1"></div>
                </div>

                {/* Botón de toggle integrado - Más pequeño y sutil */}
                <button
                    onClick={() => setIsMinimized(!isMinimized)}
                    className={`
                        flex items-center justify-center rounded-full text-gray-400 
                        hover:text-white hover:bg-white/10 transition-all duration-300
                        ${isMinimized ? 'w-10 h-10 p-2 bg-white/5' : 'w-6 h-6 p-1 ml-1'}
                    `}
                    title={isMinimized ? "Maximizar menú" : "Minimizar menú"}
                >
                    {isMinimized ? (
                        <ChevronUp className="w-5 h-5 text-white animate-pulse" />
                    ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                    )}
                </button>
            </div>
        </div>
    );
}
